import { maintenanceStore } from '../maintenance/maintenanceStore';
import { isMaintenanceError, isSuccessfulWrite, maintenanceFromError, shouldRecheckAfter, } from '../maintenance/maintenanceState';
import { createApi, fetchBaseQuery, } from '@reduxjs/toolkit/query/react';
import Config from 'react-native-config';
import { backendBaseUrlSelector, setCredentials, signout, } from '../redux/appSlice';
import { DEFAULT_ENVIRONMENT, resolveBackendBaseUrl, } from '../config/environment';
import { SCREENSHOT_MODE } from '../screenshotMode';
import { screenshotFixtureResponse } from '../screenshotMode/fixtures';
const normalizeBaseUrl = (url) => url.endsWith('/') ? url.slice(0, -1) : url;
const ensureLeadingSlash = (path) => path.startsWith('/') ? path : `/${path}`;
const resolveApiBaseUrl = (state) => {
    const fallback = Config.BACKEND_BASE_URL || resolveBackendBaseUrl(DEFAULT_ENVIRONMENT);
    const configured = backendBaseUrlSelector(state) || fallback;
    return `${normalizeBaseUrl(configured)}/api/v1`;
};
/** Guest-only auth calls: do not attach a persisted session token (stale JWTs can break these). */
const AUTH_ENDPOINTS_WITHOUT_SESSION = new Set([
    'login',
    'registerUser',
    'confirmEmail',
    'resendConfirmation',
    'changePendingEmail',
    'forgotPassword',
    'resetPassword',
    'requestPublicAccountDeletion',
]);
const rawBaseQuery = fetchBaseQuery({
    prepareHeaders: (headers, { getState, endpoint }) => {
        // endpoint is available
        const rootState = getState();
        const skipSessionBearer = typeof endpoint === 'string' &&
            AUTH_ENDPOINTS_WITHOUT_SESSION.has(endpoint);
        // Check if this is a refresh call by looking for our custom header.
        const isRefresh = headers.get('x-refresh') === 'true';
        if (isRefresh) {
            // Remove the custom header so it isn’t sent to the backend.
            headers.delete('x-refresh');
            const token = rootState.app.refreshToken;
            if (token) {
                headers.set('authorization', `Bearer ${token}`);
            }
        }
        else if (!skipSessionBearer) {
            const token = rootState.app.userToken;
            if (token) {
                headers.set('authorization', `Bearer ${token}`);
            }
        }
        if (endpoint !== 'uploadPhoto') {
            // Assuming 'uploadPhoto' is the endpoint name for FormData
            headers.set('Content-Type', 'application/json');
        }
        return headers;
    },
});
// Custom base query wrapper to support token refresh.
const baseQueryWithReauth = async (args, api, extraOptions) => {
    const state = api.getState();
    const baseUrl = resolveApiBaseUrl(state);
    // For non-auth endpoints, check if the token is expired.
    if (typeof args === 'string') {
        args = { url: args };
    }
    // App Store screenshot mode: serve fixtures, never touch the network.
    if (SCREENSHOT_MODE) {
        return screenshotFixtureResponse(args);
    }
    if (!args.url.includes('/auth/')) {
        const rootState = api.getState();
        const currentTokenExpires = rootState.app.tokenExpires;
        // Check token expiry (assumed to be in milliseconds here)
        if (currentTokenExpires && Date.now() >= currentTokenExpires) {
            // Proceed with refresh
            const currentRefreshToken = rootState.app.refreshToken;
            if (currentRefreshToken) {
                const refreshResult = await rawBaseQuery({
                    url: `${baseUrl}/auth/refresh`,
                    method: 'POST',
                    // Use a custom header to signal that we want to use the refresh token
                    headers: {
                        'x-refresh': 'true', // custom flag header
                        // note: do not set authorization here, let prepareHeaders handle it.
                    },
                }, api, extraOptions);
                if (refreshResult.data) {
                    // Destructure the refresh response and dispatch setCredentials.
                    const { token, refreshToken, tokenExpires } = refreshResult.data;
                    api.dispatch(setCredentials({ token, refreshToken, tokenExpires }));
                }
                else {
                    // Refresh failed: sign out.
                    api.dispatch(signout());
                    return refreshResult;
                }
            }
            else {
                api.dispatch(signout());
            }
        }
    }
    const finalArgs = typeof args === 'string'
        ? { url: `${baseUrl}${ensureLeadingSlash(args)}` }
        : {
            ...args,
            url: args.url
                ? /^https?:\/\//i.test(args.url)
                    ? args.url
                    : `${baseUrl}${ensureLeadingSlash(args.url)}`
                : args.url,
        };
    // Proceed with the original request.
    const result = await rawBaseQuery(finalArgs, api, extraOptions);
    // Maintenance mode (docs/MAINTENANCE_MODE_STORIES.md): the API's 503 maintenance
    // reply shows the maintenance UI at once; other network/5xx failures re-check the flag.
    if (!result.error && isSuccessfulWrite(finalArgs)) {
        // The server accepted a write, so it isn't in maintenance (stale banner safety net).
        maintenanceStore.writeSucceeded();
    }
    if (result.error) {
        if (isMaintenanceError(result.error)) {
            maintenanceStore.report(maintenanceFromError(result.error));
            maintenanceStore.check({ force: true }).catch(() => undefined);
        }
        else if (shouldRecheckAfter(result.error)) {
            maintenanceStore.check().catch(() => undefined);
        }
    }
    // If any API call returns a 401 and it is not an auth call, sign out.
    if (result.error && result.error.status === 401) {
        // Optionally check args.url to avoid signing out on an /auth/ call.
        const urlToCheck = (finalArgs && typeof finalArgs.url === 'string'
            ? finalArgs.url
            : undefined) ||
            (typeof args === 'string' ? args : args.url) ||
            '';
        if (!urlToCheck.includes('/auth/')) {
            api.dispatch(signout());
        }
    }
    return result;
};
// -----------------------------------------------------
// RTK Query API Definition
// -----------------------------------------------------
export const abbiWayApi = createApi({
    invalidationBehavior: 'immediately',
    reducerPath: 'abbiWayApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: [
        'Meal',
        'Biometrics',
        'User',
        'UsersList',
        'UserEvent',
        'Photos',
        'Food',
        'Favorites',
        'IssueReport',
        'MealMap',
        'WeeklyNetDown',
    ],
    endpoints: build => ({
        // ----- Auth Endpoints -----
        login: build.mutation({
            query: credentials => ({
                url: '/auth/email/login',
                method: 'POST',
                body: credentials,
            }),
        }),
        addUser: build.mutation({
            query: data => ({
                url: '/auth/email/add',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [{ type: 'UsersList', id: 'LIST' }],
        }),
        registerUser: build.mutation({
            query: data => ({
                url: '/auth/email/register',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [{ type: 'UsersList', id: 'LIST' }],
        }),
        confirmEmail: build.mutation({
            query: ({ hash }) => ({
                url: '/auth/email/confirm',
                method: 'POST',
                body: { hash },
            }),
        }),
        resendConfirmation: build.mutation({
            query: body => ({
                url: '/auth/email/resend-confirmation',
                method: 'POST',
                body,
            }),
        }),
        changePendingEmail: build.mutation({
            query: body => ({
                url: '/auth/email/change-pending-email',
                method: 'POST',
                body,
            }),
        }),
        forgotPassword: build.mutation({
            query: body => ({
                url: '/auth/forgot/password',
                method: 'POST',
                body,
            }),
        }),
        requestPublicAccountDeletion: build.mutation({
            query: body => ({
                url: '/auth/deletion-request',
                method: 'POST',
                body,
            }),
        }),
        resetPassword: build.mutation({
            query: body => ({
                url: '/auth/reset/password',
                method: 'POST',
                body,
            }),
        }),
        // ----- Other Endpoints -----
        getUser: build.query({
            query: id => ({
                url: `users/${id}`,
                method: 'GET',
            }),
            providesTags: (result, error, id) => [{ type: 'User', id }],
        }),
        getMe: build.query({
            query: () => ({
                url: 'auth/me',
                method: 'GET',
            }),
            providesTags: result => (result ? [{ type: 'User', id: result.id }] : []),
        }),
        requestAccountDeletion: build.mutation({
            query: () => ({
                url: 'auth/me/deletion-request',
                method: 'POST',
            }),
        }),
        updateMe: build.mutation({
            query: body => ({
                url: 'auth/me',
                method: 'PATCH',
                body,
            }),
            async onQueryStarted(patch, { dispatch, queryFulfilled }) {
                if (patch.heightCm == null && patch.athleteMode === undefined) {
                    return;
                }
                const patchResult = dispatch(abbiWayApi.util.updateQueryData('getMe', undefined, draft => {
                    if (!draft) {
                        return;
                    }
                    if (!draft.profile) {
                        draft.profile = {};
                    }
                    if (patch.heightCm != null) {
                        draft.profile.heightCm = patch.heightCm;
                    }
                    if (patch.athleteMode !== undefined) {
                        draft.profile.athleteMode = patch.athleteMode;
                    }
                }));
                try {
                    const { data } = await queryFulfilled;
                    dispatch(abbiWayApi.util.updateQueryData('getMe', undefined, () => data));
                }
                catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: result => result ? [{ type: 'User', id: result.id }] : [],
        }),
        getUsers: build.query({
            query: ({ search, includeArchived } = {}) => {
                const params = new URLSearchParams();
                params.append('limit', '100');
                if (search) {
                    params.append('search', search);
                }
                if (includeArchived) {
                    params.append('filters', JSON.stringify({ includeArchived: true }));
                }
                return `users/?${params.toString()}`;
            },
            providesTags: result => result?.data
                ? [
                    ...result.data.map(({ id }) => ({ type: 'User', id })),
                    { type: 'UsersList', id: 'LIST' },
                ]
                : [{ type: 'UsersList', id: 'LIST' }],
        }),
        updateUser: build.mutation({
            query: args => ({
                url: `users/${args.userId}`,
                method: 'PATCH',
                body: args.payload,
            }),
            invalidatesTags: (result, error, { userId }) => [
                { type: 'User', id: userId },
                { type: 'UsersList', id: 'LIST' },
            ],
        }),
        // Delete user endpoint (no auth token passed in manually)
        // permanent: true for hard delete, false/undefined for archive (soft delete)
        deleteUser: build.mutation({
            query: ({ userId, permanent }) => ({
                url: `users/${userId}${permanent ? '?permanent=true' : ''}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { userId }) => [
                { type: 'User', id: userId },
                { type: 'UsersList', id: 'LIST' },
            ],
        }),
        // Check if a user is eligible for permanent deletion
        checkUserDeletionEligibility: build.query({
            query: userId => ({
                url: `users/${userId}/deletion-eligibility`,
                method: 'GET',
            }),
        }),
        // Restore (unarchive) a soft-deleted user
        restoreUser: build.mutation({
            query: userId => ({
                url: `users/${userId}/restore`,
                method: 'POST',
            }),
            invalidatesTags: (result, error, userId) => [
                { type: 'User', id: userId },
                { type: 'UsersList', id: 'LIST' },
            ],
        }),
        dismissAccountDeletionRequest: build.mutation({
            query: userId => ({
                url: `users/${userId}/deletion-request/dismiss`,
                method: 'POST',
            }),
            invalidatesTags: (result, error, userId) => [
                { type: 'User', id: userId },
                { type: 'UsersList', id: 'LIST' },
            ],
        }),
        // Generate individualized program (admin): 1–2 user IDs, optional template (men/women), optional startDate
        generateProgram: build.mutation({
            query: body => ({
                url: 'admin/individualized-program/generate',
                method: 'POST',
                body,
            }),
        }),
        getFood: build.query({
            query: id => ({
                url: `foods/${id}`,
                method: 'GET',
            }),
        }),
        getFoods: build.query({
            query: ({ limit = 50, page = 1, search }) => ({
                url: search
                    ? `foods/?limit=${limit}&page=${page}&search=${search}`
                    : `foods/?limit=${limit}&page=${page}`,
                method: 'GET',
            }),
            providesTags: result => result?.data
                ? [
                    ...result.data.map(({ awfId }) => ({
                        type: 'Food',
                        id: awfId,
                    })),
                    { type: 'Food', id: 'LIST' },
                ]
                : [{ type: 'Food', id: 'LIST' }],
        }),
        createFood: build.mutation({
            query: newFood => ({
                url: 'foods',
                method: 'POST',
                body: newFood,
            }),
            invalidatesTags: [{ type: 'Food', id: 'LIST' }],
        }),
        // New endpoint for searching FDC foods
        searchFdcFoods: build.query({
            query: ({ query }) => ({
                url: `foods/search-fdc?query=${encodeURIComponent(query)}`,
                method: 'GET',
            }),
            // This data is external, so no need to invalidate local tags
            // unless you want to cache search results, which might be complex.
        }),
        // New endpoint for importing an FDC food into our local database
        importFdcFood: build.mutation({
            query: ({ fdcId }) => ({
                url: 'foods/import-fdc',
                method: 'POST',
                body: { fdcId },
            }),
            // Invalidate the food list so the newly imported food appears in searches
            // and other food listings.
            invalidatesTags: [{ type: 'Food', id: 'LIST' }],
        }),
        suggestFavorites: build.query({
            query: ({ userId, q, mealSlot, date }) => `favorites/suggest?userId=${userId}&q=${encodeURIComponent(q)}&mealSlot=${encodeURIComponent(mealSlot)}&date=${date}`,
            providesTags: [{ type: 'Favorites', id: 'LIST' }],
        }),
        deleteFavorite: build.mutation({
            query: ({ id }) => ({
                url: `favorites/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Favorites', id: 'LIST' }],
        }),
        createMealEntry: build.mutation({
            query: body => {
                const bodyAsJsonString = JSON.stringify(body, null, 2);
                // Log the curl command for debugging/testing purposes
                console.log(`
--- createMealEntry CURL ---
curl -X POST "[dynamic-base-url]/api/v1/mealEntries" \\
  -H "Content-Type: application/json" \\
  -d '${bodyAsJsonString}'
--------------------------`);
                return { url: 'mealEntries', method: 'POST', body };
            },
            invalidatesTags: (result, error, { date }) => {
                // Get userId from the response (backend returns raw database row with user_id field)
                const userId = result?.user_id;
                if (!userId || !date) {
                    // Fallback: invalidate all meal-related tags if userId or date not available
                    return [
                        { type: 'Meal', id: 'LIST' },
                        { type: 'Favorites', id: 'LIST' },
                    ];
                }
                return [
                    { type: 'Meal', id: 'LIST' },
                    { type: 'Meal', id: `STATUS-${userId}-${date}` },
                    { type: 'Meal', id: `SUMMARY-${userId}` }, // Invalidate summary cache
                    { type: 'Favorites', id: 'LIST' }, // Invalidate suggestions
                ];
            },
        }),
        deleteMealEntry: build.mutation({
            query: ({ id }) => ({
                url: `mealEntries/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: result => {
                if (!result?.userId || !result?.date) {
                    return [
                        { type: 'Meal', id: 'LIST' },
                        { type: 'Favorites', id: 'LIST' },
                    ];
                }
                return [
                    { type: 'Meal', id: 'LIST' },
                    { type: 'Meal', id: `STATUS-${result.userId}-${result.date}` },
                    { type: 'Meal', id: `SUMMARY-${result.userId}` },
                    { type: 'Favorites', id: 'LIST' },
                ];
            },
        }),
        getMealEntries: build.query({
            query: ({ userId, date }) => `mealEntries?userId=${userId}&date=${date}`,
            providesTags: [{ type: 'Meal', id: 'LIST' }],
        }),
        getMealStatus: build.query({
            query: ({ userId, date }) => `mealEntries/status?userId=${userId}&date=${date}`,
            // Invalidate when any meal list changes, as this affects status.
            // Also provides a specific tag for this user/date combo for targeted invalidation.
            providesTags: (result, error, { userId, date }) => result ? [{ type: 'Meal', id: `STATUS-${userId}-${date}` }] : [],
        }),
        getMealSummary: build.query({
            query: ({ userId, month }) => {
                const params = new URLSearchParams({ userId: userId.toString() });
                if (month) {
                    params.append('month', month);
                }
                return `mealEntries/summary?${params.toString()}`;
            },
            // Force refetch to get updated totalMonthMeals
            keepUnusedDataFor: 0,
            providesTags: (result, error, { userId }) => result ? [{ type: 'Meal', id: `SUMMARY-${userId}` }] : [],
        }),
        getTrends: build.query({
            query: ({ userId, period = '1m' }) => {
                const params = new URLSearchParams({ userId: userId.toString() });
                params.append('period', period);
                return `mealEntries/trends?${params.toString()}`;
            },
            providesTags: (result, error, { userId }) => result ? [{ type: 'Meal', id: `TRENDS-${userId}` }] : [],
        }),
        getMealMap: build.query({
            query: ({ userId, startDate, days }) => ({
                url: `mealMap/?userId=${userId}&startDate=${startDate}&days=${days}`,
                method: 'GET',
            }),
            providesTags: (result, error, arg) => [
                { type: 'MealMap', id: `${arg.userId}-${arg.startDate}` },
            ],
        }),
        getAdminWeeklyNetDown: build.query({
            query: ({ userId, clientToday }) => {
                const params = new URLSearchParams({ userId: String(userId) });
                if (clientToday) {
                    params.append('clientToday', clientToday);
                }
                return {
                    url: `admin/weekly-net-down?${params.toString()}`,
                    method: 'GET',
                };
            },
            providesTags: result => result
                ? [{ type: 'WeeklyNetDown', id: result.userId }]
                : ['WeeklyNetDown'],
        }),
        getMyWeeklyNetDown: build.query({
            query: arg => {
                const clientToday = arg && 'clientToday' in arg ? arg.clientToday : undefined;
                const params = new URLSearchParams();
                if (clientToday) {
                    params.append('clientToday', clientToday);
                }
                const qs = params.toString();
                return {
                    url: qs ? `me/weekly-net-down?${qs}` : 'me/weekly-net-down',
                    method: 'GET',
                };
            },
            providesTags: result => result ? [{ type: 'WeeklyNetDown', id: result.userId }] : ['WeeklyNetDown'],
        }),
        getCalorieWeightAnalysis: build.query({
            query: ({ userId, window = '1m', clientToday }) => ({
                url: `admin/calorie-weight-analysis?userId=${userId}&window=${window}&clientToday=${encodeURIComponent(clientToday)}`,
                method: 'GET',
            }),
        }),
        generateCalorieWeightAiBrief: build.mutation({
            query: ({ userId, window = '1m', clientToday }) => ({
                url: 'admin/calorie-weight-analysis/ai-brief',
                method: 'POST',
                body: { userId, window, clientToday },
            }),
        }),
        getProgramState: build.query({
            query: ({ userId, date }) => {
                const params = new URLSearchParams({ userId: userId.toString() });
                if (date) {
                    params.append('date', date);
                }
                return { url: `programState?${params.toString()}`, method: 'GET' };
            },
        }),
        validateMealEntry: build.query({
            query: body => ({
                url: '/meals/validate',
                method: 'POST',
                body,
            }),
        }),
        getBiometrics: build.query({
            query: date => ({
                url: date.date ? `biometrics?date=${date.date}` : 'biometrics',
                method: 'GET',
            }),
            providesTags: (result, error, arg) => arg.date
                ? [{ type: 'Biometrics', id: `DATE-${arg.date}` }]
                : ['Biometrics'],
        }),
        createBiometrics: build.mutation({
            query: ({ date, measurements }) => ({
                url: 'biometrics',
                method: 'POST',
                body: { date, measurements },
            }),
            invalidatesTags: (result, error, arg) => [
                'Biometrics',
                { type: 'Biometrics', id: `DATE-${arg.date}` },
                'MealMap',
                'WeeklyNetDown',
            ],
        }),
        finishDay: build.mutation({
            query: body => ({
                url: 'days',
                method: 'POST',
                body,
            }),
        }),
        createUserEvent: build.mutation({
            query: userEvent => ({
                url: 'userEvents',
                method: 'POST',
                body: userEvent,
            }),
            // Invalidate the specific user's list of events
            invalidatesTags: (result, error, arg) => result ? [{ type: 'UserEvent', id: 'LIST', userId: arg.forUserId }] : [],
        }),
        getUserEvents: build.query({
            query: ({ userId, limit = 50, page = 1 }) => ({
                url: `userEvents/?forUserId=${userId}&limit=${limit}&page=${page}`,
                method: 'GET',
            }),
            // Provide a user-specific list tag and individual event tags
            providesTags: (result, error, { userId }) => result?.data
                ? [
                    ...result.data.map(({ id }) => ({
                        type: 'UserEvent',
                        id: id?.toString(),
                    })),
                    { type: 'UserEvent', id: 'LIST', userId },
                ]
                : [{ type: 'UserEvent', id: 'LIST', userId }],
        }),
        deleteUserEvent: build.mutation({
            query: ({ userEventId }) => ({
                url: `userEvents/${userEventId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { userEventId, forUserId }) => [
                { type: 'UserEvent', id: userEventId.toString() },
                { type: 'UserEvent', id: 'LIST', userId: forUserId },
            ],
        }),
        // ----- Photos Endpoints -----
        uploadPhoto: build.mutation({
            query: ({ file, photoType, caption, forUserId }) => {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('type', photoType);
                if (caption) {
                    formData.append('caption', caption);
                }
                formData.append('forUserId', String(forUserId));
                return {
                    url: 'photos/upload',
                    method: 'POST',
                    body: formData,
                    // RTK Query handles FormData content type automatically
                };
            },
            invalidatesTags: (result, _error, _arg) => result ? [{ type: 'Photos', id: 'LIST', userId: result.userId }] : [],
        }),
        deletePhoto: build.mutation({
            query: ({ photoId }) => ({
                url: `photos/${photoId}`,
                method: 'DELETE',
            }),
            // When a photo is deleted, a 'photoDeleted' user event is created.
            // Invalidate both the list of photos and the list of user events for that user.
            invalidatesTags: (_result, _error, arg) => [
                { type: 'Photos', id: 'LIST', userId: arg.userId },
                { type: 'UserEvent', id: 'LIST', userId: arg.userId },
            ],
        }),
        getPhotosByUserId: build.query({
            query: userId => ({
                url: `photos/user/${userId}`,
                method: 'GET',
            }),
            providesTags: (result, error, userId) => result?.data
                ? [
                    ...result.data.map(({ id }) => ({ type: 'Photos', id })),
                    { type: 'Photos', id: 'LIST', userId }, // Tag for invalidating list by userId
                ]
                : [{ type: 'Photos', id: 'LIST', userId }],
        }),
        // ----- Issue Report Endpoint -----
        reportIssue: build.mutation({
            query: payload => ({
                url: 'issues/report',
                method: 'POST',
                body: payload,
            }),
        }),
        // ----- Day type overrides -----
        upsertDayTypeOverride: build.mutation({
            query: ({ date, dayType, clientToday }) => ({
                url: `users/me/day-type-overrides/${date}`,
                method: 'PUT',
                body: { dayType, clientToday },
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'Meal', id: `STATUS-${arg.userId}-${arg.date}` },
                { type: 'MealMap', id: 'LIST' },
            ],
        }),
        clearDayTypeOverride: build.mutation({
            query: ({ date, clientToday }) => ({
                url: `users/me/day-type-overrides/${date}?clientToday=${encodeURIComponent(clientToday)}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'Meal', id: `STATUS-${arg.userId}-${arg.date}` },
                { type: 'MealMap', id: 'LIST' },
            ],
        }),
    }),
});
// ------------------------------------------------------------------
// Export auto-generated hooks for usage in functional components
// ------------------------------------------------------------------
export const { useLoginMutation, useGetMeQuery, useUpdateMeMutation, useRequestAccountDeletionMutation, useAddUserMutation, useRegisterUserMutation, useConfirmEmailMutation, useResendConfirmationMutation, useChangePendingEmailMutation, useForgotPasswordMutation, useRequestPublicAccountDeletionMutation, useResetPasswordMutation, useDeleteUserMutation, useCheckUserDeletionEligibilityQuery, useLazyCheckUserDeletionEligibilityQuery, useRestoreUserMutation, useDismissAccountDeletionRequestMutation, useGenerateProgramMutation, useGetFoodQuery, useLazyGetFoodQuery, useGetFoodsQuery, useLazyGetFoodsQuery, useCreateFoodMutation, useSearchFdcFoodsQuery, useLazySearchFdcFoodsQuery, useImportFdcFoodMutation, useLazySuggestFavoritesQuery, useCreateMealEntryMutation, useDeleteMealEntryMutation, useGetUserQuery, useGetUsersQuery, useLazyGetUsersQuery, useUpdateUserMutation, useGetMealMapQuery, useLazyGetMealMapQuery, useGetAdminWeeklyNetDownQuery, useGetMyWeeklyNetDownQuery, useLazyGetMyWeeklyNetDownQuery, useGetCalorieWeightAnalysisQuery, useGenerateCalorieWeightAiBriefMutation, useLazyGetMealEntriesQuery, useGetMealStatusQuery, useLazyGetMealStatusQuery, useLazyValidateMealEntryQuery, useLazyGetBiometricsQuery, useGetProgramStateQuery, useLazyGetProgramStateQuery, useCreateBiometricsMutation, useFinishDayMutation, useGetMealSummaryQuery, useLazyGetMealSummaryQuery, useGetTrendsQuery, useLazyGetTrendsQuery, useCreateUserEventMutation, useGetUserEventsQuery, useDeleteUserEventMutation, useDeleteFavoriteMutation, useLazyGetUserEventsQuery, useUploadPhotoMutation, useGetPhotosByUserIdQuery, useLazyGetPhotosByUserIdQuery, useDeletePhotoMutation, useReportIssueMutation, useUpsertDayTypeOverrideMutation, useClearDayTypeOverrideMutation, } = abbiWayApi;
// Utility function to invalidate summary cache for a specific user
export const invalidateSummaryCache = (dispatch, userId) => {
    dispatch(abbiWayApi.util.invalidateTags([{ type: 'Meal', id: `SUMMARY-${userId}` }]));
};
